import { useEffect } from "react";

/**
 * CosmicCursor
 * -------------------------------------------------------
 * Spawns warm honey/brass dust-mote particles that trail
 * the cursor ONLY while it is over elements marked with
 * `data-cosmic="true"` (or any ancestor with that attr).
 *
 * - Zero React re-renders (direct DOM manipulation)
 * - Respects prefers-reduced-motion
 * - Auto-cleans particles after their animation finishes
 */
export default function CosmicCursor() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) return;
    // no-op on touch-primary devices
    if (window.matchMedia("(hover: none)").matches) return;

    // dedicated overlay so we don't ever interfere with the page
    const layer = document.createElement("div");
    layer.setAttribute("aria-hidden", "true");
    layer.style.cssText =
      "position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:60;";
    document.body.appendChild(layer);

    // warm palette — brass · honey · sunlight · terracotta · dawnrose
    const COLORS = ["#E0B84C", "#F5D77E", "#B08830", "#B8622A", "#E6A79B"];
    let lastX = 0;
    let lastY = 0;
    let lastSpawn = 0;
    const MIN_INTERVAL = 24; // ms — throttle particle spawning

    const spawn = (x, y) => {
      const size = 4 + Math.random() * 6;
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];
      const dx = (Math.random() - 0.5) * 40;
      const dy = -30 - Math.random() * 60;
      const rot = (Math.random() - 0.5) * 40;
      const dur = 900 + Math.random() * 700;

      const el = document.createElement("span");
      el.style.cssText = `
        position:absolute;
        left:${x}px;
        top:${y}px;
        width:${size}px;
        height:${size}px;
        margin-left:${-size / 2}px;
        margin-top:${-size / 2}px;
        border-radius:9999px;
        background:radial-gradient(circle, ${color} 0%, rgba(0,0,0,0) 70%);
        filter:blur(0.4px);
        opacity:0.9;
        transform:translate3d(0,0,0) scale(1) rotate(0deg);
        transition:transform ${dur}ms cubic-bezier(0.22,1,0.36,1), opacity ${dur}ms ease-out;
        will-change:transform,opacity;
      `;
      layer.appendChild(el);

      // next frame → animate out
      requestAnimationFrame(() => {
        el.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(0.35) rotate(${rot}deg)`;
        el.style.opacity = "0";
      });

      setTimeout(() => {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, dur + 60);
    };

    const onMove = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      const target = e.target;
      if (!target || !target.closest) return;
      const hit = target.closest('[data-cosmic="true"]');
      if (!hit) return;
      const now = performance.now();
      if (now - lastSpawn < MIN_INTERVAL) return;
      lastSpawn = now;

      // 1–2 motes per tick for a delicate density
      const count = Math.random() < 0.35 ? 2 : 1;
      for (let i = 0; i < count; i++) {
        spawn(lastX + (Math.random() - 0.5) * 8, lastY + (Math.random() - 0.5) * 8);
      }
    };

    window.addEventListener("pointermove", onMove, { passive: true });

    return () => {
      window.removeEventListener("pointermove", onMove);
      if (layer.parentNode) layer.parentNode.removeChild(layer);
    };
  }, []);

  return null;
}
